import { FormBuilder } from '@/components/formBuilder';
import { Flex, Box, Link, Spinner, Center } from '@chakra-ui/react';
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  getContactInitialValuesFromResume,
  checkContactMatching,
  hasValidLinkedInUrl,
} from './CreateContactHelpers';
import { useParams, useHistory } from 'react-router-dom';
import { useAuth, useShowToast } from '@/hooks';
import { useCreateContactConfig } from './useCreateContactConfig';
import { ContactResumeUpload } from './ContactResumeUpload';
import { Drawer } from '../drawer';
import dayjs from 'dayjs';
import { useCreateContactMutation } from '@/store/query/contacts-list';
import { getDegreeDetails } from './api';
import { useQuery } from '@tanstack/react-query';

function generateCustomPayload(obj) {
  const customObject = {};
  const payload = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('custom_')) {
      const newKey = key.replace('custom_', '');
      customObject[newKey] = value;
    } else {
      payload[key] = value;
    }
  }

  return {
    customObject,
    payload,
  };
}

function extractKeysToGroupedArray(data, keyMapping) {
  const grouped = {};

  // Iterate over each key prefix to process the keys in the data
  keyMapping.forEach(({ keyPrefix, propertyName }, _index) => {
    Object.keys(data).forEach((key) => {
      if (key.startsWith(keyPrefix)) {
        const id = key.split('_').pop();
        grouped[id] = grouped[id] || {};
        grouped[id][propertyName] = data[key];
      }
    });
  });

  return Object.values(grouped);
}

export const CreateContact = ({
  modalActions = {},
  additionalInitialValues = {},
  isCtaLoading = false,
  defaults = [],
  refetch = undefined,
  projectId = undefined,
  refetchData = () => {},
  setTriggerRef = () => {},
  refetchDelay = 0,
  redirectToContactOnCreate = false,
}) => {
  const { onClose } = modalActions;
  const formRef = useRef();
  const { jobId } = useParams();
  const history = useHistory();
  const toast = useShowToast();
  const [parsedResume, setParsedResume] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [isDeletedContact, setDeleted] = useState(false);
  const [duplicateContact, setDuplicateContact] = useState(false);
  const [hasLinkedInUrl, setHasLinkedInUrl] = useState(false);

  // Observer to check if LinkedIn URL is filled in the socials section
  const handleFormChange = useCallback((formValues) => {
    setHasLinkedInUrl(hasValidLinkedInUrl(formValues));
  }, []);

  // Track API loading states - only run when config should load
  const { data: getDegree, isLoading: isDegreeLoading } = useQuery({
    queryKey: ['get_degrees'],
    queryFn: () => getDegreeDetails(),
  });

  // Only generate form config after  delay
  const { globalFormConfig, isLoading } = useCreateContactConfig({
    parsedResume,
    getDegree,
    formRef,
    defaults,
    setDeleted,
    isDeletedContact,
    setDuplicateContact,
    duplicateContact,
    hasLinkedInUrl,
  });

  const { currUser } = useAuth();
  const [createContact] = useCreateContactMutation();
  const [isCreateLoading, setIsCreateLoading] = useState(false);

  // Show spinner while waiting for delay or while APIs are loading or config is not ready
  const isModalLoading =
    modalActions?.isOpen && (isDegreeLoading || !globalFormConfig || isLoading);

  useEffect(() => {
    const getInitialVal = async () => {
      const resumeValue = await getContactInitialValuesFromResume(
        parsedResume?.resume,
        getDegree
      );

      // Check for deleted contact BEFORE setting initial values
      if (resumeValue?.email) {
        const deletedContact = await checkContactMatching(
          resumeValue.first_name || '',
          resumeValue.last_name || '',
          resumeValue.email
        );

        if (deletedContact?.deleted) {
          setDeleted({
            ...deletedContact,
            deletedBannerForEmailMatch: true,
          });
        } else {
          // Clear the deleted state if no matching deleted contact is found
          setDeleted(false);
        }
      }

      const values = {
        //add previous values also
        ...formRef?.current?.values,

        //by default populate the current user for credited to
        credited_to_user_id: currUser?.id,

        //get initial values from job, flows, projects
        ...additionalInitialValues,

        //initial value from resume
        ...resumeValue,

        time_zone: resumeValue?.phone_number
          ? resumeValue?.time_zone
          : resumeValue?.time_zone ||
            currUser?.time_zone ||
            currUser?.account?.time_zone ||
            undefined,

        resume_parser_job_id: parsedResume?.config?.generation_id,
      };

      setInitialValues(values);
    };
    getInitialVal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, parsedResume, currUser]);

  const duplicateApplicationErrMessage = (contactId) => (
    <Box>
      Candidate already exists in this job.{' '}
      {contactId && (
        <Link
          color="base-white"
          href={`/candidates/${contactId}`}
          textDecoration="underline"
          isExternal
          fontWeight="bold"
        >
          View candidate
        </Link>
      )}
    </Box>
  );

  const duplicateEmailErrMessage = (contactId) => (
    <Box>
      Candidate with this email already exists.{' '}
      {contactId && (
        <Link
          color="base-white"
          href={`/candidates/${contactId}`}
          textDecoration="underline"
          isExternal
          fontWeight="bold"
        >
          View candidate
        </Link>
      )}
    </Box>
  );

  const handleSubmitCreate = async (values) => {
    const {
      email,
      first_name,
      last_name,
      linkedin_url,
      location,
      phone_number,
      time_zone,
      skills,
      resume_parser_job_id,
      ats_candidate_source_id,
      credited_to_user_id,
      tags,
      outreach_ids,
      project_ids,
      ...others
    } = values;

    const jobKeyMapping = [
      { keyPrefix: 'jobs_stage_', propertyName: 'ats_job_stage_id' },
      { keyPrefix: 'jobs_job_', propertyName: 'ats_job_id' },
    ];
    const jobDetails = extractKeysToGroupedArray(others, jobKeyMapping);

    const socialsKeyMapping = [
      { keyPrefix: 'socials_kind_', propertyName: 'kind' },
      { keyPrefix: 'socials_url_', propertyName: 'url' },
    ];
    const socialDetails = extractKeysToGroupedArray(others, socialsKeyMapping);

    const educationKeyMapping = [
      { keyPrefix: 'education_startDate_', propertyName: 'start_date' },
      { keyPrefix: 'education_endDate_', propertyName: 'end_date' },
      { keyPrefix: 'education_degree_', propertyName: 'academic_degree_id' },
      {
        keyPrefix: 'education_institution_',
        propertyName: 'academic_institution_id',
      },
    ];

    const educations = extractKeysToGroupedArray(others, educationKeyMapping)
      .filter((edu) => edu?.academic_degree_id && edu?.academic_institution_id)
      .map((i) => ({
        ...i,
        start_date: i?.start_date
          ? dayjs(i?.start_date).format('YYYY-MM-DD')
          : null,
        end_date: i?.end_date ? dayjs(i.end_date).format('YYYY-MM-DD') : null,
      }));

    const experienceKeyMapping = [
      { keyPrefix: 'experience_title_', propertyName: 'title' },
      { keyPrefix: 'experience_company_', propertyName: 'company_id' },
      { keyPrefix: 'experience_endDate_', propertyName: 'end_date' },
      { keyPrefix: 'experience_startDate_', propertyName: 'start_date' },
    ];
    const experiences = extractKeysToGroupedArray(others, experienceKeyMapping)
      .filter((exp) => exp?.title && exp?.company_id)
      .map((i) => ({
        ...i,
        start_date: i?.start_date
          ? dayjs(i?.start_date)?.format('YYYY-MM-DD')
          : null,
        end_date: i?.end_date ? dayjs(i.end_date)?.format('YYYY-MM-DD') : null,
      }));

    const { customObject } = generateCustomPayload(others);

    const payload = {
      update_existing: true,
      email,
      first_name,
      last_name,
      linkedin_url,
      location,
      resume_parser_job_id,
      ats_candidate_source_id,
      credited_to_user_id,
      outreach_ids,
      project_ids,
      ...(isDeletedContact ? { undelete_existing: true } : {}),
    };

    if (Object.keys(customObject)?.length > 0) {
      payload.additional_info = {
        ...customObject,
      };
    }

    if (tags?.length > 0) {
      payload.tags = tags?.join(',');
    }

    if (skills?.length > 0) {
      payload.skills = skills?.join(',');
    }
    if (
      phone_number &&
      typeof phone_number === 'object' &&
      phone_number?.number
    ) {
      payload.phone_number = phone_number.number.trim().replace(/\s/g, '');
      payload.phone_code = phone_number.dialCode;
      payload.location_iso = phone_number.countryCode ?? null;
    }
    if (time_zone !== undefined && time_zone !== null && time_zone !== '') {
      payload.time_zone = time_zone;
    }
    if (projectId !== undefined) {
      payload.project_ids = [Number(projectId)];
    }
    if (jobDetails?.length > 0) {
      payload.job_details = jobDetails;
    }
    if (socialDetails?.length > 0) {
      payload.social_urls = socialDetails;
    }

    if (experiences?.length > 0) {
      payload.experiences = experiences;
    }
    if (educations?.length > 0) {
      payload.educations = educations;
    }

    try {
      setIsCreateLoading(true);
      const { data, error } = await createContact({ payload });
      if (data?.contact?.id) {
        // Standalone create: skip the delayed list refetch (we're navigating
        // away) and go straight to the new candidate's detail page.
        if (redirectToContactOnCreate) {
          setIsCreateLoading(false);
          toast({ title: `Contact created`, duration: 6000 });
          onClose();
          history.push(`/candidates/${data.contact.id}`);
          return;
        }
        setTimeout(() => {
          setTriggerRef('create_contact');
          refetch?.(data?.contact);
          if (projectId) {
            refetchData();
          }
          setIsCreateLoading(false);
          toast({ title: `Contact created`, duration: 6000 });
          onClose();
        }, refetchDelay);
      } else {
        setIsCreateLoading(false);
        const message = error?.[0]?.msg;
        const contactId = error?.[0]?.contact_id;
        const error_code = error?.[0]?.err_code;
        const duplicateEmailContactId = error?.[0]?.duplicate_email_contact_id;

        if (
          error?.[0] ===
          'Contact experiences is invalid, User contacts contact contact experiences is invalid'
        ) {
          toast({
            title: 'Invalid candidate experience. Review it and try again.',
            status: 'error',
          });
          return;
        }

        let errorTitle = `Unable to create candidate`;
        let errorDescription = message;

        if (
          error_code === 'err_contact_already_exists' &&
          duplicateEmailContactId
        ) {
          errorTitle = duplicateEmailErrMessage(duplicateEmailContactId);
          errorDescription = undefined;
        } else if (error_code === 'err_candidate_already_exists_in_job') {
          errorTitle = duplicateApplicationErrMessage(contactId);
          errorDescription = undefined;
        }

        toast({
          title: errorTitle,
          description: errorDescription,
          status: 'error',
        });
      }
    } catch (error) {
      setIsCreateLoading(false);
      console.error('Error creating contact:', error);
      toast({
        title: `An error occurred`,
        description: 'Unable to create contact. Please try again.',
        status: 'error',
      });
    }
  };

  const renderContent = () => {
    return (
      <Flex
        flexDir="column"
        w="100%"
        h="100%"
        overflowY="scroll"
        overflowX="hidden"
        alignItems="center"
      >
        <ContactResumeUpload setParsedResume={setParsedResume} />
        <Box m="0em 10em" h="100%" w="100%">
          <FormBuilder
            initialValues={initialValues}
            config={globalFormConfig}
            onSubmit={handleSubmitCreate}
            formRef={formRef}
            isObserveEnabled={true}
            observFunc={handleFormChange}
          />
        </Box>
      </Flex>
    );
  };

  const handleDrawerSubmit = () => {
    if (formRef.current) {
      const errors = formRef.current?.errors || {};
      const [key, value] = Object.entries(errors)?.[0] || [];
      if (key) {
        const element = document.getElementById(key);
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        toast({
          title: value,
          status: 'error',
        });
      }
      formRef.current.handleSubmit();
    }
  };

  return (
    <Flex>
      {modalActions?.isOpen && (
        <Drawer
          drawerActions={modalActions}
          headerText="Create new candidate"
          headerDescription="Fill the necessary information to create a new candidate."
          ctaLabel="Create"
          isCtaLoading={isCreateLoading || isCtaLoading}
          ctaProps={{ isDisabled: Boolean(duplicateContact?.id) }}
          onDrawerSubmit={handleDrawerSubmit}
        >
          {isModalLoading ? (
            <Center h="100vh">
              <Spinner size="lg" color="secondary-700" />
            </Center>
          ) : (
            renderContent()
          )}
        </Drawer>
      )}
    </Flex>
  );
};
